/**
 * Wizard scaffolding.
 *
 * Both multi-step flows are route based rather than state based, so each step is
 * linkable, refreshable and independently testable. The draft itself lives in
 * `stores/drafts`, which is what lets steps be visited in any order.
 */

import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { cn } from '../../lib/format'
import { Button } from '../ui/button'

export interface WizardStep {
  key: string
  label: string
  to: string
}

export interface WizardStepsProps {
  steps: WizardStep[]
  current: string
  className?: string
}

export function WizardSteps({ steps, current, className }: WizardStepsProps) {
  const currentIndex = steps.findIndex((step) => step.key === current)

  return (
    <ol className={cn('flex flex-wrap items-center gap-2 text-xs', className)}>
      {steps.map((step, index) => {
        const isCurrent = step.key === current
        const isDone = currentIndex > index
        return (
          <li key={step.key} className="flex items-center gap-2">
            <Link
              to={step.to}
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                // A step is a navigation target on a phone, so it keeps a touch
                // target above the bare text height.
                'flex min-h-8 items-center gap-1.5 rounded-full border px-2.5 py-1 transition-colors',
                isCurrent
                  ? 'border-primary bg-primary text-primary-foreground'
                  : isDone
                    ? 'border-border bg-secondary text-secondary-foreground'
                    : 'border-border text-muted-foreground hover:bg-accent',
              )}
            >
              <span className="font-medium">{index + 1}</span>
              <span>{step.label}</span>
            </Link>
          </li>
        )
      })}
    </ol>
  )
}

export interface WizardFooterProps {
  backTo?: string
  backLabel: string
  nextLabel: string
  onNext?: () => void
  nextDisabled?: boolean
  nextType?: 'button' | 'submit'
  busy?: boolean
  hint?: ReactNode
  className?: string
}

/** Back link on the left, the advancing action on the right. */
export function WizardFooter({
  backTo,
  backLabel,
  nextLabel,
  onNext,
  nextDisabled,
  nextType = 'button',
  busy,
  hint,
  className,
}: WizardFooterProps) {
  return (
    <div className={cn('flex flex-col gap-2 pt-2', className)}>
      <div className="flex items-center justify-between gap-3">
        {backTo ? (
          <Button variant="ghost" asChild>
            <Link to={backTo}>{backLabel}</Link>
          </Button>
        ) : (
          <span />
        )}

        {/* Always a real button: the advancing action validates the step, so it
            cannot be a link, and a disabled link is not disabled at all. */}
        <Button type={nextType} onClick={onNext} disabled={nextDisabled || busy}>
          {nextLabel}
        </Button>
      </div>
      {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
    </div>
  )
}
